import { useEffect, useMemo } from "react";
import {
  DEFAULT_OG_IMAGE_URL,
  GOOGLE_SITE_VERIFICATION,
  getAbsoluteUrl,
  getOptimizedImageUrl,
  SITE_DESCRIPTION,
  SITE_LOCALE,
  SITE_LOGO_URL,
  SITE_NAME,
  SITE_TWITTER_HANDLE,
} from "../../constant/site";

const cleanText = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const limitText = (value, limit) => {
  const text = cleanText(value);
  return text.length > limit ? `${text.slice(0, limit - 3).trim()}...` : text;
};

const upsertMeta = (attribute, key, content) => {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!content) {
    element?.remove();
    return;
  }

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
};

const upsertCanonical = (href) => {
  if (!href) {
    return;
  }

  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
};

const removeExistingMetaList = (attribute, key) => {
  document.head
    .querySelectorAll(`meta[${attribute}="${key}"]`)
    .forEach((element) => element.remove());
};

const appendMetaList = (attribute, key, values) => {
  removeExistingMetaList(attribute, key);

  values.filter(Boolean).forEach((content) => {
    const element = document.createElement("meta");
    element.setAttribute(attribute, key);
    element.setAttribute("content", content);
    document.head.appendChild(element);
  });
};

const removeEmptyValues = (value) => {
  if (Array.isArray(value)) {
    return value.map(removeEmptyValues).filter(Boolean);
  }

  if (value && typeof value === "object") {
    return Object.entries(value).reduce((acc, [key, entry]) => {
      const cleaned = removeEmptyValues(entry);
      if (
        cleaned !== undefined &&
        cleaned !== "" &&
        (!Array.isArray(cleaned) || cleaned.length)
      ) {
        acc[key] = cleaned;
      }
      return acc;
    }, {});
  }

  return value;
};

const upsertArticleSchema = (article, meta) => {
  const scriptId = "news-article-schema";
  const existingScript = document.getElementById(scriptId);

  if (!article) {
    existingScript?.remove();
    return;
  }

  const logoUrl = getAbsoluteUrl(SITE_LOGO_URL || DEFAULT_OG_IMAGE_URL);
  const script = existingScript || document.createElement("script");
  script.id = scriptId;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(
    removeEmptyValues({
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: article.headline || meta.title,
      description: meta.description,
      image: meta.image ? [meta.image] : [],
      datePublished: article.datePublished,
      dateModified: article.dateModified || article.datePublished,
      author: article.author
        ? [
            {
              "@type": "Person",
              name: article.author,
            },
          ]
        : [],
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        logo: logoUrl
          ? {
              "@type": "ImageObject",
              url: logoUrl,
            }
          : undefined,
      },
      articleSection: article.section,
      keywords: meta.keywords,
      mainEntityOfPage: meta.url,
    })
  );

  if (!existingScript) {
    document.head.appendChild(script);
  }
};


const upsertBreadcrumbSchema = (breadcrumbs = []) => {
  const scriptId = "breadcrumb-schema";
  const existingScript = document.getElementById(scriptId);
  const items = breadcrumbs.filter((item) => item?.name && item?.url);

  if (!items.length) {
    existingScript?.remove();
    return;
  }

  const script = existingScript || document.createElement("script");
  script.id = scriptId;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: getAbsoluteUrl(item.url),
    })),
  });

  if (!existingScript) {
    document.head.appendChild(script);
  }
};
function Seo({
  title,
  description,
  keywords,
  image,
  imageAlt,
  url,
  shareUrl,
  type = "website",
  robots = "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
  article,
}) {
  const keywordList = useMemo(
    () =>
      Array.isArray(keywords)
        ? keywords.map(cleanText).filter(Boolean)
        : cleanText(keywords)
            .split(",")
            .map(cleanText)
            .filter(Boolean),
    [keywords]
  );
  const keywordContent = keywordList.join(", ");
  const metaTitle = title ? `${limitText(title, 70)} | ${SITE_NAME}` : SITE_NAME;
  const metaDescription = limitText(description || SITE_DESCRIPTION, 160);
  const metaImage = getAbsoluteUrl(getOptimizedImageUrl(image || DEFAULT_OG_IMAGE_URL));
  const canonicalUrl = getAbsoluteUrl(url || window.location.pathname);
  const metaUrl = getAbsoluteUrl(shareUrl || canonicalUrl);
  const twitterHandle = SITE_TWITTER_HANDLE
    ? `@${SITE_TWITTER_HANDLE.replace(/^@/, "")}`
    : "";

  useEffect(() => {
    document.title = metaTitle;

    upsertMeta("name", "description", metaDescription);
    upsertMeta("name", "keywords", keywordContent);
    upsertMeta("name", "robots", robots);
    upsertMeta("name", "google-site-verification", GOOGLE_SITE_VERIFICATION);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:locale", SITE_LOCALE);
    upsertMeta("property", "og:title", metaTitle);
    upsertMeta("property", "og:description", metaDescription);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", metaUrl);
    upsertMeta("property", "og:image", metaImage);
    upsertMeta("property", "og:image:secure_url", metaImage);
    upsertMeta("property", "og:image:alt", imageAlt || title || SITE_NAME);
    upsertMeta("name", "twitter:card", metaImage ? "summary_large_image" : "summary");
    upsertMeta("name", "twitter:site", twitterHandle);
    upsertMeta("name", "twitter:title", metaTitle);
    upsertMeta("name", "twitter:description", metaDescription);
    upsertMeta("name", "twitter:image", metaImage);
    upsertMeta("name", "twitter:image:alt", imageAlt || title || SITE_NAME);

    if (article) {
      upsertMeta("property", "article:published_time", article.datePublished);
      upsertMeta("property", "article:modified_time", article.dateModified);
      upsertMeta("property", "article:author", article.author);
      upsertMeta("property", "article:section", article.section);
      appendMetaList("property", "article:tag", keywordList);
    } else {
      upsertMeta("property", "article:published_time", "");
      upsertMeta("property", "article:modified_time", "");
      upsertMeta("property", "article:author", "");
      upsertMeta("property", "article:section", "");
      removeExistingMetaList("property", "article:tag");
    }

    upsertCanonical(canonicalUrl);
    upsertBreadcrumbSchema(article?.breadcrumbs);
    upsertArticleSchema(article, {
      title: metaTitle,
      description: metaDescription,
      image: metaImage,
      url: canonicalUrl,
      keywords: keywordContent,
    });
  }, [
    article,
    canonicalUrl,
    imageAlt,
    keywordContent,
    keywordList,
    metaDescription,
    metaImage,
    metaTitle,
    metaUrl,
    robots,
    title,
    twitterHandle,
    type,
  ]);

  return null;
}

export default Seo;